/**
 * **Channel x day ingest coverage** — one cell per channel per day.
 *
 * Moved out of `pages/ChannelOverview.tsx` on 10 Sep (CO-2). It was a third tab
 * on Channel Overview, which put a *data-completeness* question next to
 * *publishing-performance* charts: nobody asking "did we get 20 July?" goes
 * looking under Channel Overview. It now expands inside the Snap card on
 * `/connections`, beside that pipeline's own strip — the estate-wide strip
 * answers *which days*, this answers *which channels on those days*.
 *
 * **Not a duplicate of `CoverageStrip`.** That is one row for the whole estate;
 * this is 13. The strip tells you a day is incomplete, this tells you which
 * channel made it so.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../lib/api'
import { formatAxisDate, formatDate } from '../lib/dates'
import { HintBubble } from './Hint'
import { Card, Note, Skeleton } from './ui'

/**
 * How many cells a date label covers — the clearance the end of the axis needs.
 *
 * `08/19/26` at `0.6rem` in Plus Jakarta Sans is ~42px; a cell is `0.55rem`
 * (~8.8px) plus 2px of padding. 42 / 10.8 ≈ 3.9, and the final label is
 * *right*-anchored so it grows back across its own neighbours — one more cell of
 * margin, rounded up. **Change the label format or the cell width and this is
 * the number to recompute**, which is why it is one named constant rather than a
 * literal inside the condition.
 */
const LABEL_CELLS = 6

/**
 * The ledger's own words, in the client's.
 *
 * 🔴 **`empty` is the one that must not read as a failure.** It means we asked
 * Snap and Snap had nothing — the channel published nothing that day — and
 * `snap.ingest_coverage` exists precisely so that is distinguishable from a day
 * we never collected. Printing the raw status made the two look like variants of
 * the same problem.
 */
const COVERAGE_WORDS: Record<string, string> = {
  complete: 'Collected',
  empty: 'Nothing published that day',
  failed: 'Collection failed',
  missing: 'Never collected',
}

const COVERAGE_COLOURS: Record<string, string> = {
  complete: 'var(--status-good)',
  empty: 'var(--status-warning)',
  failed: 'var(--status-critical)',
}

export function ChannelCoverageGrid({ from, to, channels }: { from: string; to: string; channels: string[] }) {
  const coverage = useQuery({
    queryKey: ['channel-coverage', from, to, channels.join(',')],
    queryFn: () => api.channelCoverage({ from, to, channels }),
  })
  const registry = useQuery({ queryKey: ['channels'], queryFn: api.channels, staleTime: Infinity })

  /**
   * One bubble, driven by delegated `mouseover` — the pattern `ViewsHeatmap`
   * already uses, and for the same reason.
   *
   * These cells carried a native `title`, which is what `Hint` exists to
   * replace: ~1s before the browser shows anything, in the OS font, on a grid
   * where hovering is the **only** way to read a value. 12 channels x 90 days
   * is ~1,080 cells, so a `Hint` per cell would mount 1,080 components and
   * 1,080 pieces of state for one bubble at a time.
   */
  const [hover, setHover] = useState<{ rect: DOMRect; label: string; value: string } | null>(
    null,
  )

  if (coverage.isLoading || registry.isLoading) return <Skeleton rows={5} />
  if (coverage.error) return <Note tone="error">{(coverage.error as Error).message}</Note>

  const cells = coverage.data ?? []
  const days = [...new Set(cells.map((cell) => cell.stat_date))].sort()
  const byChannel = new Map<string, Map<string, string>>()
  for (const cell of cells) {
    if (!byChannel.has(cell.profile_id)) byChannel.set(cell.profile_id, new Map())
    byChannel.get(cell.profile_id)!.set(cell.stat_date, cell.status)
  }

  const rows = (registry.data ?? []).filter(
    (channel) => channels.length === 0 || channels.includes(channel.handle),
  )

  return (
    <Card
      title="Channel × Day Coverage"
      info="Which Channels Are Missing Data, and on Which Days."
    >
      <p className="mt-0 mb-3 text-xs" style={{ color: 'var(--text-label)' }}>
        {cells.length} channel-days ·{' '}
        {['complete', 'empty', 'failed'].map((status) => {
          const count = cells.filter((cell) => cell.status === status).length
          return count ? (
            <span key={status} className="mr-3">
              <span
                className="mr-1 inline-block align-middle"
                style={{
                  width: '0.6rem',
                  height: '0.6rem',
                  borderRadius: '2px',
                  background: COVERAGE_COLOURS[status],
                }}
              />
              {count} {status}
            </span>
          ) : null
        })}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{ borderCollapse: 'collapse' }}
          onMouseOver={(event) => {
            const cell = (event.target as HTMLElement).closest('td[data-label]')
            if (!cell) return
            setHover({
              rect: cell.getBoundingClientRect(),
              label: cell.getAttribute('data-label') ?? '',
              value: cell.getAttribute('data-value') ?? '',
            })
          }}
          onMouseLeave={() => setHover(null)}
        >
          <caption className="sr-only">
            Ingest Coverage per Channel per Day for Profile_stats_daily
          </caption>
          <thead>
            {/* A date axis, sparsely labelled. Without it the grid proves
                capture but does not say *when* — and "did we get 20 July?" is
                the question this view exists to answer. */}
            <tr>
              <td />
              {days.map((day, index) => (
                <th
                  key={day}
                  scope="col"
                  className="text-[0.6rem] font-normal"
                  // Same width as a cell, and the label is absolutely
                  // positioned so it cannot widen its column: laid out
                  // normally, seven labels stretched the grid into uneven
                  // week-shaped clumps.
                  style={{
                    width: '0.55rem',
                    padding: '0 1px',
                    position: 'relative',
                    height: '1rem',
                  }}
                >
                  {/* 🔴 **Anchored right at the end of the axis.**
                      Every label is absolutely positioned so it cannot widen
                      its 0.55rem column, which means it overhangs ~30px to the
                      right of the cell it belongs to. On the *last* cell there
                      is nothing to overhang into, so the date ran off the edge
                      of the grid and was clipped — worse since the format went
                      from `Aug 19` to `08/19/26`.

                      Anchoring the final label to `right: 0` grows it leftward
                      instead. And a weekly label too close to the end is
                      dropped, or the two overlap into an unreadable smear.

                      🔴 **CN-6 — that clearance was four cells, and four is not
                      enough.** Abi, 15 Sep: *"when you expand per channel
                      detail, all the dates squish on top of each other towards
                      the end of the table."* The number was measured when a
                      label read `Aug 19`; it now reads `08/19/26`, which is
                      ~42px against a 0.55rem (~8.8px) cell — **five cells wide,
                      not four.** At the 90-day default the last weekly label
                      lands on index 84 and the right-anchored final label
                      occupies roughly 85–90, so they overlap by three cells.

                      Derived from the two widths rather than re-guessed, so it
                      cannot rot again the next time the format changes. */}
                  {(index === 0
                    || index === days.length - 1
                    || (index % 7 === 0 && index < days.length - LABEL_CELLS)) && (
                    <span
                      style={{
                        position: 'absolute',
                        ...(index === days.length - 1 ? { right: 0 } : { left: 0 }),
                        bottom: '0.1rem',
                        whiteSpace: 'nowrap',
                        color: 'var(--axis-label)',
                      }}
                    >
                      {formatAxisDate(day)}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((channel) => (
              <tr key={channel.profile_id}>
                <th
                  scope="row"
                  className="pr-3 text-xs font-medium"
                  style={{
                    color: 'var(--text-secondary)',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {channel.display_label}
                </th>
                {days.map((day) => {
                  const status = byChannel.get(channel.profile_id)?.get(day)
                  return (
                    <td
                      key={day}
                      style={{ padding: '1px' }}
                      data-label={`${channel.display_label} · ${formatDate(day)}`}
                      data-value={COVERAGE_WORDS[status ?? 'missing'] ?? (status ?? 'no row')}
                    >
                      <span
                        className="block"
                        style={{
                          width: '0.55rem',
                          height: '0.9rem',
                          borderRadius: '1px',
                          background: status
                            ? COVERAGE_COLOURS[status] ?? 'var(--status-idle)'
                            : 'var(--bg-page)',
                          border: status ? 'none' : '1px solid var(--border)',
                        }}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {hover && (
          <HintBubble rect={hover.rect}>
            <strong style={{ display: 'block' }}>{hover.label}</strong>
            <span style={{ color: 'var(--text-secondary)' }}>{hover.value}</span>
          </HintBubble>
        )}
      </div>
    </Card>
  )
}
